import React, { useMemo, useState, useEffect } from "react";
import { View, Text, StyleSheet, Pressable, ActivityIndicator } from "react-native";
import Svg, { Circle } from "react-native-svg";
import { getDaysInMonth } from "date-fns";
import { UserService } from "../../services/UserService";
import { Flash } from "../utils/Flash";

type Filter = "Day" | "Month";
type RingStatus = "red" | "orange" | "green";

type UserScoreDto = {
  marksObtained: number;
  totalMarks: number;
  dailyMinMarks: number;
};

type ScoreCardProps = {
  refreshKey?: number;
  onRefreshComplete?: () => void;
};

export function ScoreCard({ refreshKey = 0, onRefreshComplete }: ScoreCardProps) {
  const [filter, setFilter] = useState<Filter>("Day");
  const [open, setOpen] = useState(false);

  const [score, setScore] = useState<number>(0);
  const [target, setTarget] = useState<number>(0);
  const [minimumTarget, setMinimumTarget] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const scoreType = filter === "Day" ? "daily" : "monthly";

  useEffect(() => {
    fetchScore(scoreType);
  }, [scoreType, refreshKey]);

  const fetchScore = async (type: string) => {
    try {
      setLoading(true);

      const res = await UserService.getScore(type);

      if (res.status !== 200) {
        Flash.error(res.message);
        return;
      }

      const data: UserScoreDto = res.data ?? {
        marksObtained: 0,
        totalMarks: 0,
        dailyMinMarks: 0,
      };

      const marksObtained = Number(data?.marksObtained ?? 0);
      const totalMarks = Number(data?.totalMarks ?? 0);
      const dailyMinMarks = Number(data?.dailyMinMarks ?? 0);

      if (type === "daily") {
        setScore(marksObtained);
        setTarget(totalMarks);
        setMinimumTarget(dailyMinMarks);
      } else {
        const monthDays = getDaysInMonth(new Date());

        setScore(marksObtained);
        setTarget(totalMarks * monthDays);
        setMinimumTarget(dailyMinMarks * monthDays);
      }
    } catch (error) {
      console.log("Score API error", error);
      setScore(0);
      setTarget(0);
      setMinimumTarget(0);
    } finally {
      setLoading(false);
      onRefreshComplete?.();
    }
  };

  const percentage = useMemo(() => {
    if (!target) return 0;
    return Math.max(0, Math.min(100, (score / target) * 100));
  }, [score, target]);

  const ringStatus = useMemo<RingStatus>(() => {
    if (score < minimumTarget) {
      return "red";
    }

    if (score >= target) {
      return "green";
    }

    return "orange";
  }, [score, minimumTarget, target]);

  const ringColors = useMemo(() => {
    if (ringStatus === "red") {
      return {
        track: "rgba(239,68,68,0.18)",
        stroke: "#EF4444",
        text: "#B91C1C",
      };
    }

    if (ringStatus === "green") {
      return {
        track: "rgba(34,197,94,0.18)",
        stroke: "#22C55E",
        text: "#15803D",
      };
    }

    return {
      track: "rgba(251,146,60,0.20)",
      stroke: "#f0873c",
      text: "rgba(124,45,18,0.85)",
    };
  }, [ringStatus]);

  const radius = 24;
  const stroke = 6;
  const cx = 32;
  const cy = 32;

  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (percentage / 100) * circumference;

  return (
    <View style={styles.wrap}>
      <View style={styles.card}>
        <View style={styles.row}>
          <View>
            <Text style={styles.caption}>
              {filter === "Day" ? "TODAY'S SCORE" : "MONTHLY SCORE"}
            </Text>

            <View style={styles.scoreRow}>
              {loading ? (
                <ActivityIndicator size="small" color="#F97316" />
              ) : (
                <>
                  <Text style={styles.big}>{score}</Text>
                  <Text style={styles.small}>/ {target}</Text>
                </>
              )}
            </View>

            {/* {!loading ? (
              <Text style={styles.metaText}>
                Min Target: {minimumTarget}
              </Text>
            ) : null} */}
          </View>

          <View style={styles.ringWrap}>
            <Svg width={64} height={64} style={{ transform: [{ rotate: "-90deg" }] }}>
              <Circle
                cx={cx}
                cy={cy}
                r={radius}
                stroke={ringColors.track}
                strokeWidth={stroke}
                fill="transparent"
              />

              <Circle
                cx={cx}
                cy={cy}
                r={radius}
                stroke={ringColors.stroke}
                strokeWidth={stroke}
                fill="transparent"
                strokeDasharray={`${circumference} ${circumference}`}
                strokeDashoffset={dashOffset}
                strokeLinecap="round"
              />
            </Svg>

            <View style={styles.ringText}>
              <Text style={[styles.ringPct, { color: ringColors.text }]}>
                {Math.round(percentage)}%
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.dropdown}>
          <Pressable
            onPress={() => {
              setFilter("Day");
              setOpen(false);
            }}
            style={[styles.dropBtn, filter === "Day" && styles.dropOn]}
          >
            <Text style={[styles.dropText, filter === "Day" && styles.dropTextOn]}>
              Daily
            </Text>
          </Pressable>

          <Pressable
            onPress={() => {
              setFilter("Month");
              setOpen(false);
            }}
            style={[styles.dropBtn, filter === "Month" && styles.dropOn]}
          >
            <Text style={[styles.dropText, filter === "Month" && styles.dropTextOn]}>
              Monthly
            </Text>
          </Pressable>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { width: "100%" },

  card: {
    width: "100%",
    backgroundColor: "rgba(255,255,255,0.85)",
    borderRadius: 18,
    padding: 18,
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.18)",
    shadowColor: "#000",
    shadowOpacity: 0.08,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 8 },
    elevation: 3,
  },

  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },

  filterRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },

  caption: {
    fontSize: 11,
    fontWeight: "900",
    color: "rgba(234,88,12,0.95)",
    letterSpacing: 1,
    paddingLeft: 8,
  },

  chev: {
    fontSize: 10,
    fontWeight: "900",
    color: "rgba(234,88,12,0.75)",
  },

  scoreRow: {
    flexDirection: "row",
    alignItems: "baseline",
    gap: 6,
    marginTop: 6,
    paddingLeft: 8,
  },

  big: {
    fontSize: 25,
    fontWeight: "900",
    color: "#4A2311",
  },

  small: {
    fontSize: 12,
    fontWeight: "800",
    color: "rgba(107,114,128,0.85)",
  },

  metaText: {
    marginTop: 4,
    paddingLeft: 8,
    fontSize: 11,
    fontWeight: "700",
    color: "rgba(107,114,128,0.95)",
  },

  ringWrap: {
    width: 60,
    height: 80,
    alignItems: "center",
    justifyContent: "center",
  },

  ringText: {
    position: "absolute",
    alignItems: "center",
    justifyContent: "center",
  },

  ringPct: {
    fontSize: 11,
    fontWeight: "900",
  },

  dropdown: {
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "rgba(251,146,60,0.18)",
    flexDirection: "row",
    gap: 19,
    padding: 5,
  },

  dropBtn: {
    flex: 1,
    height: 36,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.18)",
  },

  dropOn: {
    backgroundColor: "rgba(251,146,60,0.12)",
  },

  dropText: {
    fontSize: 12,
    fontWeight: "800",
    color: "rgba(75,85,99,0.9)",
  },

  dropTextOn: {
    color: "rgba(234,88,12,0.98)",
  },
});